/**
 * Internal move helpers shared between `moves.ts` and `win.ts`.
 *
 * Kept separate to avoid a circular import — `win.ts` needs to know whether
 * Mr. X has any legal move, but `moves.ts` calls `win.ts` after applying.
 */

import type { GameState, Transport } from '../types'
import type { Adjacency } from './board'

/** True if a station is occupied by a non-stranded detective. */
export function isOccupiedByDetective(state: GameState, station: number): boolean {
  return state.detectives.some(
    (d) => !d.stranded && d.station === station,
  )
}

/**
 * Enumerate every legal Mr. X *single* sub-move from `mrxStation`. This is
 * shared between:
 *   - `legalMoves` (Mr. X's normal turn, both single & first half of double)
 *   - `hasAnyMrxMove` (the cornered-detection used by `win.ts`)
 *
 * Yields `{ toStation, transport, useBlackTicket }`. A given destination/
 * transport pair may appear multiple times: once with the natural ticket and
 * once with `useBlackTicket = true` (if Mr. X has any black tickets left).
 * Ferry edges only appear with `useBlackTicket = true`.
 */
export function* mrxSubMoves(
  state: GameState,
  mrxStation: number,
  adjacency: Adjacency,
): Generator<{
  toStation: number
  transport: Transport
  useBlackTicket: boolean
}> {
  const edges = adjacency.get(mrxStation) ?? []

  // Mr. X's basic-transport tickets (taxi/bus/underground) are treated as
  // effectively unlimited, per rules.md: "he cannot run out of basic-transport
  // tickets in practice" (used basic tickets return to his pool). The only
  // gate on Mr. X's basic-transport moves is that the edge must exist with the
  // right transport type. If you want to enforce a starting allotment,
  // augment `MrxPublicState` with basic-ticket counts and check them here.

  for (const e of edges) {
    if (isOccupiedByDetective(state, e.to)) continue

    if (e.transport === 'ferry') {
      if (state.mrx.blackTickets > 0) {
        yield { toStation: e.to, transport: 'ferry', useBlackTicket: true }
      }
      continue
    }

    // Standard ticket move (taxi/bus/underground) — always available since
    // basic tickets are unlimited.
    yield { toStation: e.to, transport: e.transport, useBlackTicket: false }
    // Black-ticket substitution.
    if (state.mrx.blackTickets > 0) {
      yield { toStation: e.to, transport: e.transport, useBlackTicket: true }
    }
  }
}

/**
 * True if Mr. X has at least one legal single sub-move from `mrxStation`.
 * Used by `win.ts` to detect the "cornered" condition.
 */
export function hasAnyMrxMove(
  state: GameState,
  mrxStation: number,
  adjacency: Adjacency,
): boolean {
  const iter = mrxSubMoves(state, mrxStation, adjacency)
  return !iter.next().done
}
